import {
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Spinner,
    useMediaQuery
} from '@chakra-ui/react';
import { EventType } from '@components/Event';
import EventQuestions from '@components/Event/EventQuestions';
import { useLivestreamData } from '@components/Event/provider/LivestreamDataProvider';
import Timeline, { TimelineScroller } from '@components/EventSchedules/Timeline';
import UserAllowedSchedules from '@components/EventSchedules/UserAllowedSchedules';
import UserPresenceEventSchedule from '@components/EventSchedules/UserPresenceEventSchedule';
import LiveExpoAds from '@components/Player/LiveExpoAds';
import TedLiveDrawer from '@components/Ted/TedLiveDrawer';
import { useAnalytics, useWatchTimeAnalytics } from '@lib/analytics';
import { generateTreeSchedule } from '@lib/data-transform/event-schedules';
import { useViewerQualification } from '@lib/firebase';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { FaExpandAlt, FaPlayCircle, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';
import ReactPlayer from 'react-player/lazy';

import AdaptivePlayerLayout, { AdaptivePlayerLayoutHandle } from './AdaptivePlayerLayout';
import useVolumeSupported from './hooks/useVolumeSupported';
import LiveMessage from './LiveMessage';
import LiveTicker from './LiveTicker';
import PresenceControl from './PresenceControl';

const LivestreamPlayer: FunctionComponent<{ event: EventType; url: string }> = ({ event, url }) => {
    const [isTimelineInline] = useMediaQuery(['(min-width: 600px)', '(min-aspect-ratio: 16/9)']);

    const adaptivePlayerLayoutRef = useRef<AdaptivePlayerLayoutHandle>(null);
    const schedulesTree = generateTreeSchedule(event.event_schedules);
    const isVolumeSupported = useVolumeSupported();
    const [playerVolume, setPlayerVolume] = useState<number>(1);
    const [playerUrl, setPlayerUrl] = useState('');
    const [playerMuted, setPlayerMuted] = useState(true);
    const [playerControls, setPlayerControls] = useState(playerMuted);
    const [qualifyUserAsLivestreamViewer] = useViewerQualification(event.id, 'livestream');
    const isFullscreen = useRef(false);
    const { currentProgram, eventAccess } = useLivestreamData();

    const { analytics } = useAnalytics();

    const sendWatchTimeAnalytics = useWatchTimeAnalytics({
        _category: 'livestream',
        _action: 'watch_time',
        event_id: event.id,
        event_schedule_id: currentProgram?.id
    });

    const isPlaying = !!playerUrl;

    useEffect(() => {
        if (currentProgram && eventAccess.canViewSchedule(currentProgram, 'livestream')) {
            setPlayerUrl(url);
        } else {
            setPlayerUrl('');
        }
    }, [currentProgram, eventAccess]);

    /**
     * Called when media starts or resumes playing after pausing or buffering
     */
    function onPlay() {
        if (currentProgram && eventAccess.canViewSchedule(currentProgram, 'livestream')) {
            analytics?.send('player_play', {
                _category: 'livestream',
                _action: 'play',
                event_id: event.id,
                event_schedule_id: currentProgram.id
            });

            sendWatchTimeAnalytics('start');
        }

        qualifyUserAsLivestreamViewer();
    }

    /**
     * Callback containing played and loaded progress as a fraction, and playedSeconds and loadedSeconds in seconds
     *
     *  eg { played: 0.12, playedSeconds: 11.3, loaded: 0.34, loadedSeconds: 16.7 }
     */
    function onProgress() {
        if (currentProgram && eventAccess.canViewSchedule(currentProgram, 'livestream')) {
            sendWatchTimeAnalytics('progress');
        }
    }

    /**
     * Called when media is paused
     */
    function onPause() {
        sendWatchTimeAnalytics('stop');
    }

    /**
     * Called when media finishes playing (Does not fire when loop is set to true)
     */
    function onEnded() {
        sendWatchTimeAnalytics('stop');
    }

    /**
     * Called when media starts buffering
     */
    function onBuffer() {
        sendWatchTimeAnalytics('stop');
    }

    function play() {
        setPlayerMuted(false);
        setPlayerUrl(url);
        setPlayerControls(false);
    }

    function toggleFullScreen() {
        const mainEl = adaptivePlayerLayoutRef?.current?.mainEl;
        if (mainEl) {
            if (isFullscreen.current) {
                try {
                    document.exitFullscreen();
                } catch (e) {
                    mainEl.style.maxHeight = '';
                }
                isFullscreen.current = false;
            } else {
                try {
                    mainEl.requestFullscreen();
                } catch (e) {
                    mainEl.style.maxHeight = '100vh';
                    adaptivePlayerLayoutRef?.current?.scrollToMain();
                }
                isFullscreen.current = true;
            }
        }
    }

    return (
        <AdaptivePlayerLayout
            fixedOnScroll={isTimelineInline ? false : true}
            ref={adaptivePlayerLayoutRef}>
            <Box
                h="full"
                w="full"
                onMouseEnter={() => setPlayerControls(true)}
                onMouseLeave={() => setPlayerControls(false)}
                onTouchStart={() => setPlayerControls(true)}>
                <ReactPlayer
                    volume={playerVolume ? playerVolume : 1}
                    onPlay={onPlay}
                    onProgress={onProgress}
                    onPause={onPause}
                    onEnded={onEnded}
                    onBuffer={onBuffer}
                    url={playerUrl}
                    muted={playerMuted}
                    playing={isPlaying}
                    playsinline
                    controls={false}
                    loop={false}
                    width="100%"
                    height="100%"
                    style={{
                        backgroundColor: 'black',
                        margin: '0px auto'
                    }}
                />
                <>
                    {currentProgram && eventAccess.canViewSchedule(currentProgram, 'livestream') ? (
                        <>
                            <Box
                                position="absolute"
                                zIndex={9999}
                                top="48%"
                                left={'50%'}
                                transform="translateX(-50%)">
                                {playerMuted && isPlaying ? (
                                    <Button
                                        border="2px solid white"
                                        boxShadow="lg"
                                        onClick={play}
                                        size="lg"
                                        variant="solid"
                                        borderRadius="full"
                                        colorScheme="red"
                                        aria-label="Ton ein"
                                        leftIcon={<FaVolumeUp size="24px" />}>
                                        <FormattedMessage
                                            id="player.notification.enable_volume"
                                            defaultMessage="Hier klicken zum TON einschalten"
                                        />
                                    </Button>
                                ) : null}
                                {!isPlaying ? (
                                    <IconButton
                                        boxShadow="lg"
                                        onClick={() => setPlayerUrl(url)}
                                        width="60px"
                                        height="60px"
                                        variant="solid"
                                        borderRadius="full"
                                        colorScheme="brand"
                                        aria-label="Play / Pause"
                                        icon={<FaPlayCircle size="30px" />}
                                    />
                                ) : null}
                            </Box>
                            {!playerMuted && (
                                <Box
                                    textAlign="right"
                                    minW="260px"
                                    position="absolute"
                                    zIndex={9999}
                                    right="4"
                                    bottom="4">
                                    <EventQuestions event={event} />
                                </Box>
                            )}
                            <Flex
                                flexDir="column"
                                justify="flex-end"
                                style={{
                                    paddingRight: 'env(safe-area-inset-right)',
                                    paddingLeft: 'env(safe-area-inset-left)'
                                }}
                                zIndex={11}
                                bottom="0"
                                left="0"
                                position="absolute"
                                w="full"
                                minHeight="160px"
                                pointerEvents="none">
                                {isPlaying && !playerMuted && playerControls && (
                                    <Flex
                                        justify="space-between"
                                        align="center"
                                        bg={`linear-gradient(0deg, rgba(0,0,0, 0.4) 50%, rgba(0,0,0,0) 100%)`}
                                        w="full"
                                        pt="8"
                                        pb="2"
                                        px="4"
                                        spacing="4">
                                        <HStack spacing="4" mr="2" pointerEvents="auto">
                                            <Box>
                                                <IconButton
                                                    onClick={toggleFullScreen}
                                                    aria-label="Vollbild"
                                                    size="sm"
                                                    colorScheme="brand"
                                                    variant="solid"
                                                    icon={<FaExpandAlt />}
                                                />
                                            </Box>
                                            <Box>
                                                <IconButton
                                                    onClick={() => setPlayerMuted(!playerMuted)}
                                                    aria-label="Ton ein / aus"
                                                    size="sm"
                                                    colorScheme="brand"
                                                    variant="solid"
                                                    icon={
                                                        playerMuted ? (
                                                            <FaVolumeUp />
                                                        ) : (
                                                            <FaVolumeMute />
                                                        )
                                                    }
                                                />
                                            </Box>
                                            {isVolumeSupported ? (
                                                <Box w="30vw" maxWidth="160px" minW="60px">
                                                    <Slider
                                                        defaultValue={playerVolume * 100}
                                                        value={playerVolume * 100}
                                                        mb="-1"
                                                        min={0}
                                                        max={100}
                                                        onChange={(value) => {
                                                            value > 5
                                                                ? setPlayerVolume(value / 100)
                                                                : setPlayerMuted(true);
                                                        }}>
                                                        <SliderTrack bg="brand.100">
                                                            <SliderFilledTrack bg="brand" />
                                                        </SliderTrack>
                                                        <SliderThumb boxSize={6}>
                                                            <Box
                                                                color="brand.500"
                                                                as={FaVolumeUp}
                                                            />
                                                        </SliderThumb>
                                                    </Slider>
                                                </Box>
                                            ) : null}
                                        </HStack>
                                    </Flex>
                                )}
                            </Flex>
                            <Box
                                w="full"
                                h="full"
                                position="absolute"
                                top="0"
                                left="0"
                                zIndex={9998}
                                pointerEvents="none">
                                <LiveMessage eventId={event.id}></LiveMessage>
                                <LiveTicker></LiveTicker>
                                {eventAccess.canSubmitPresenceConfirmations() ? (
                                    <PresenceControl onClickConfirm={() => setPlayerMuted(false)} />
                                ) : null}
                            </Box>
                        </>
                    ) : null}
                    {currentProgram &&
                    !eventAccess.canViewSchedule(currentProgram, 'livestream') ? (
                        <Flex
                            bg="black"
                            color="white"
                            align="center"
                            justify="center"
                            w="full"
                            h="full"
                            position="absolute"
                            top="0"
                            left="0"
                            p="2"
                            fontSize="2xl">
                            {eventAccess.isLoading() ? (
                                <Spinner />
                            ) : (
                                <UserAllowedSchedules event={event} context="livestream" />
                            )}
                        </Flex>
                    ) : null}
                </>
            </Box>
            <Box>
                <LiveExpoAds event={event} />
                <Box pos="relative">
                    <HStack
                        justify="flex-end"
                        bg={isTimelineInline ? 'transparent' : 'gray.100'}
                        w={isTimelineInline ? 'auto' : '100%'}
                        p="4"
                        position={isTimelineInline ? 'absolute' : 'fixed'}
                        right="0"
                        top={isTimelineInline ? 0 : 'auto'}
                        bottom={isTimelineInline ? 'auto' : 0}
                        zIndex="1">
                        {eventAccess.canViewMyEventPresence() ? (
                            <UserPresenceEventSchedule event={event} context="livestream" />
                        ) : null}
                    </HStack>
                    <TimelineScroller schedules={schedulesTree} event={event}>
                        <Timeline
                            event={event}
                            schedules={schedulesTree}
                            options={{ showEvaluation: true, showStreamPresence: true }}
                            context="livestream"
                        />
                    </TimelineScroller>
                </Box>
                <TedLiveDrawer event={event} />
            </Box>
        </AdaptivePlayerLayout>
    );
};

export default LivestreamPlayer;
